import React, { useMemo, useState } from 'react';
import {
  DEFAULT_REVENUE_LEVERS_INPUTS,
  DEFAULT_SLIDER_VALS,
  HOW_STEPS,
  buildSingleLevers,
  calculateCombined,
  calculateRevenueMetrics,
  formatCount,
  formatMoney,
  formatPercentPlain,
  formatSignedPercent,
} from './revenueLeversLogic';
import RevenueLeversNumberField from './RevenueLeversNumberField';
import './RevenueLevers.css';

const EFFORT_LABELS = {
  low: 'Легко',
  mid: 'Средне',
  high: 'Сложно',
};

const LEVER_REC_LABELS = {
  l: 'Наращивайте лиды — трафик, охват, новые каналы',
  c: 'Улучшайте конверсию — оффер, посадочная, скорость обработки',
  p: 'Поднимайте средний чек — апселл, пакеты, ценовые уровни',
  q: 'Увеличивайте частоту покупок — повторные продажи, подписка, лояльность',
};

const formatLeverCurrent = (row) => {
  switch (row.key) {
    case 'l':
      return `${formatCount(row.curVal)} ${'шт'}`;
    case 'c':
      return formatPercentPlain(row.curVal);
    case 'p':
      return formatMoney(row.curVal);
    case 'q':
      return `${row.curVal.toFixed(1)} ${'раз'}`;
    default:
      return '—';
  }
};

const formatLeverNeed = (row) => {
  if (!row.valid) {
    return '—';
  }

  const value = row.formatNeed(row.need);

  if (row.key === 'l') {
    return `${value} ${'шт'}`;
  }

  if (row.key === 'q') {
    return `${value} ${'раз'}`;
  }

  return value;
};

const formatLeverAbs = (row) => {
  const raw = row.formatAbs(row.need);

  if (row.key === 'l') {
    return `${raw} ${'шт'}`;
  }

  if (row.key === 'q') {
    return `${raw} ${'раз'}`;
  }

  return raw;
};

const RevenueLevers = () => {
  const locale = 'ru-RU';

  const [inputs, setInputs] = useState(DEFAULT_REVENUE_LEVERS_INPUTS);
  const [sliderVals, setSliderVals] = useState(DEFAULT_SLIDER_VALS);
  const [activeTab, setActiveTab] = useState('single');

  const setField = (key) => (value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const metrics = useMemo(() => calculateRevenueMetrics(inputs), [inputs]);
  const singleRows = useMemo(
    () => (metrics.targetValid ? buildSingleLevers(metrics) : []),
    [metrics],
  );
  const combined = useMemo(() => calculateCombined(metrics, sliderVals), [metrics, sliderVals]);

  const formatCountLocale = (value) => formatCount(value, locale);
  const formatMoneyLocale = (value) => formatMoney(value, locale);

  const breakdownInvalidBlock = (
    <div className="rl-breakdown-empty" role="status">
      <p className="rl-breakdown-empty-title">{'Цель ниже текущей выручки'}</p>
      <p className="rl-breakdown-empty-text">
        {`Сейчас выручка ${formatMoneyLocale(metrics.revenue)}, цель — ${formatMoneyLocale(metrics.target)}. Увеличьте желаемую выручку выше текущей — тогда появится расклад по рычагам.`}
      </p>
    </div>
  );

  return (
    <div className="rl-calculator">
      <div className="rl-legend">
        <div className="rl-legend-item">
          <span className="rl-legend-dot rl-legend-dot--input" />
          {'Вводите вы'}
        </div>
        <div className="rl-legend-item">
          <span className="rl-legend-dot rl-legend-dot--calc" />
          {'Считается автоматически'}
        </div>
      </div>

      <div className="rl-layout">
        <div className="rl-panel">
          <div className="rl-panel-title">{'Текущие показатели'}</div>
          <RevenueLeversNumberField
            label={'Лиды в месяц'}
            hint={'заявки, обращения'}
            note={'Все входящие обращения: звонки, заявки, переписки'}
            unit={'шт'}
            value={inputs.leads}
            onChange={setField('leads')}
            min={1}
          />
          <RevenueLeversNumberField
            label={'Конверсия в продажу'}
            hint={'лид → сделка'}
            note={'Из 100 лидов сколько становятся клиентами'}
            unit={'%'}
            value={inputs.conv}
            onChange={setField('conv')}
            min={0.1}
            max={100}
            step={0.5}
          />
          <RevenueLeversNumberField
            label={'Средний чек'}
            note={'Средняя сумма одной продажи'}
            unit={'₸'}
            value={inputs.check}
            onChange={setField('check')}
            min={1}
          />
          <RevenueLeversNumberField
            label={'Покупок от одного клиента'}
            hint={'в месяц'}
            note={'Для разовых услуг — 1. Для повторных — среднее число покупок'}
            unit={'раз'}
            value={inputs.freq}
            onChange={setField('freq')}
            min={0.1}
            step={0.1}
          />
        </div>

        <div className="rl-side">
          <div className="rl-rev-current">
            <div>
              <div className="rl-rev-label">{'Текущая выручка'}</div>
              <div className="rl-rev-value">{formatMoneyLocale(metrics.revenue)}</div>
            </div>
            <div className="rl-rev-formula">
              {'R = L × C × P × Q'}
              <br />
              <span>{metrics.formulaDetail}</span>
            </div>
          </div>

          <div className="rl-target-panel">
            <div className="rl-panel-title">{'Цель по выручке'}</div>
            <div className="rl-target-inner">
              <div className="rl-target-field">
                <RevenueLeversNumberField
                  label={'Желаемая выручка в месяц'}
                  unit={'₸'}
                  value={inputs.target}
                  onChange={setField('target')}
                  min={1}
                  inputClassName="rl-input--target"
                />
                {!metrics.targetValid ? (
                  <div className="rl-target-err">{'Цель должна быть больше текущей выручки'}</div>
                ) : null}
              </div>
              <div className="rl-target-result">
                <div className="rl-rev-label">{'Нужно вырасти'}</div>
                <div className="rl-target-val">
                  {metrics.targetValid ? `+${metrics.growthPct.toFixed(1)}%` : '—'}
                </div>
                {metrics.targetValid ? (
                  <div className="rl-growth-badge">
                    +{formatCountLocale(metrics.growthAbs)} ₸
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="rl-stats">
            <div className="rl-stat">
              <div className="rl-panel-title">{'Клиентов сейчас'}</div>
              <div className="rl-stat-value">{formatCountLocale(metrics.clients)}</div>
              <div className="rl-stat-note">{'лидов × конверсия'}</div>
            </div>
            <div className="rl-stat">
              <div className="rl-panel-title">{'Сделок в месяц'}</div>
              <div className="rl-stat-value">{formatCountLocale(metrics.deals)}</div>
              <div className="rl-stat-note">{'клиентов × частота'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="rl-section-title">{'Брейкдаун по рычагам'}</div>

      <div className="rl-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`rl-tab${activeTab === 'single' ? ' rl-tab--active' : ''}`}
          aria-selected={activeTab === 'single'}
          onClick={() => setActiveTab('single')}
        >
          {'По одному рычагу'}
        </button>
        <button
          type="button"
          role="tab"
          className={`rl-tab${activeTab === 'combined' ? ' rl-tab--active' : ''}`}
          aria-selected={activeTab === 'combined'}
          onClick={() => setActiveTab('combined')}
        >
          {'Все рычаги вместе'}
        </button>
      </div>

      {activeTab === 'single' ? (
        <div className="rl-tab-panel">
          {!metrics.targetValid ? (
            breakdownInvalidBlock
          ) : (
            <>
              <div className="rl-table-wrap">
                <table className="rl-table">
                  <thead>
                    <tr>
                      <th>{'Рычаг'}</th>
                      <th>{'Сейчас'}</th>
                      <th>{'Нужно'}</th>
                      <th>{'Изменение'}</th>
                      <th>{'Усилие'}</th>
                      <th className="rl-th-action">{'Что делать'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {singleRows.map((row) => (
                      <tr key={row.key} className={row.isBest ? 'rl-row--best' : undefined}>
                        <td>
                          <div className="rl-lever-name">
                            <span className={`rl-lever-dot rl-lever-dot--${row.key}`} />
                            <div>
                              <div className="rl-lever-title">{row.name}</div>
                              <div className="rl-lever-desc">{row.desc}</div>
                            </div>
                          </div>
                        </td>
                        <td className="rl-td-muted">{formatLeverCurrent(row)}</td>
                        <td>
                          <span className={`rl-td-need rl-td-need--${row.key}`}>
                            {formatLeverNeed(row)}
                          </span>
                        </td>
                        <td>
                          {row.valid ? (
                            <div className="rl-td-delta">
                              <div className="rl-td-pct">{formatSignedPercent(row.deltaPct)}</div>
                              <div className="rl-td-abs">{formatLeverAbs(row)}</div>
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.effort ? (
                            <span className={`rl-effort rl-effort--${row.effort}`}>
                              {EFFORT_LABELS[row.effort]}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="rl-rec-cell">
                          {LEVER_REC_LABELS[row.key]}
                          {row.isBest ? (
                            <strong className="rl-easiest"> {'← проще всего'}</strong>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="rl-note">{'Каждая строка — независимый сценарий: что будет, если вырасти только по этому рычагу, оставив остальные без изменений. Зелёная полоса слева — самый лёгкий путь к цели.'}</p>
            </>
          )}
        </div>
      ) : (
        <div className="rl-tab-panel">
          {!metrics.targetValid ? (
            breakdownInvalidBlock
          ) : (
            <>
          <div className="rl-combined-panel">
            {combined.rows.map((row) => (
              <div key={row.key} className="rl-combined-row">
                <div className="rl-combined-meta">
                  <span className={`rl-lever-dot rl-lever-dot--${row.key}`} />
                  <div>
                    <div className="rl-lever-title">{row.name}</div>
                    <div className="rl-lever-desc">
                      {row.key === 'q'
                        ? row.cur.toFixed(1)
                        : row.key === 'c'
                          ? row.cur.toFixed(1)
                          : Math.round(row.cur)}{' '}
                      {row.unit} {'сейчас'}
                    </div>
                  </div>
                </div>
                <div className="rl-combined-slider">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={sliderVals[row.key]}
                    className={`rl-range rl-range--${row.key}`}
                    onChange={(event) =>
                      setSliderVals((prev) => ({
                        ...prev,
                        [row.key]: Number(event.target.value),
                      }))
                    }
                  />
                  <span className={`rl-combined-pct rl-combined-pct--${row.key}`}>
                    +{row.pct}%
                  </span>
                </div>
                <div className="rl-combined-result">
                  <div className={`rl-combined-new rl-combined-new--${row.key}`}>
                    {row.formatNew}
                  </div>
                  <div className="rl-combined-abs">
                    {row.pct > 0
                      ? row.key === 'c'
                        ? `+${(row.newVal - row.cur).toFixed(1)} п.п.`
                        : row.key === 'p'
                          ? `+${formatMoneyLocale(row.newVal - row.cur)}`
                          : row.key === 'q'
                            ? `+${(row.newVal - row.cur).toFixed(2)} ${'раз'}`
                            : `+${formatCountLocale(row.newVal - row.cur)} ${'шт'}`
                      : 'без изменений'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rl-combined-total">
            <div>
              <div className="rl-ct-label">{'Текущая выручка'}</div>
              <div className="rl-ct-val">{formatMoneyLocale(metrics.revenue)}</div>
            </div>
            <div className="rl-ct-progress">
              <div className="rl-ct-label">{'Прогресс к цели'}</div>
              <div className="rl-ct-progress-label">
                {combined.reached
                  ? '✓ Цель достигнута'
                  : `${combined.progressLabelPct}% от цели`}
              </div>
              <div className="rl-ct-bar">
                <div
                  className={`rl-ct-bar-fill${combined.reached ? ' rl-ct-bar-fill--over' : ' rl-ct-bar-fill--under'}`}
                  style={{ width: `${combined.progress}%` }}
                />
              </div>
            </div>
            <div>
              <div className="rl-ct-label">{'Выручка при выбранном росте'}</div>
              <div className="rl-ct-val rl-ct-val--green">{formatMoneyLocale(combined.newRevenue)}</div>
            </div>
            <div>
              <div className="rl-ct-label">{'Цель'}</div>
              <div className="rl-ct-val rl-ct-val--accent">{formatMoneyLocale(metrics.target)}</div>
            </div>
          </div>
          <p className="rl-note">{'Двигайте ползунки — смотрите как меняется выручка. Показатели перемножаются, поэтому небольшой рост по каждому рычагу даёт непропорционально большой результат.'}</p>
            </>
          )}
        </div>
      )}

      <div className="rl-how">
        <div className="rl-how-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="rl-how-step">
            <div className="rl-how-num">{step.num}</div>
            <div className="rl-how-text">
              <strong>{step.title}</strong> {step.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevenueLevers;
